'use client';

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { fetchStats, locauxService, visitesService, documentsService } from '@/lib/supabase/services';
import type { AppStats } from '@/lib/supabase/services';

const MONTHLY_DATA = [
  { mois: 'Oct', locaux: 7, visites: 4, matchs: 5, loyers: 320000 },
  { mois: 'Nov', locaux: 8, visites: 5, matchs: 6, loyers: 410000 },
  { mois: 'Déc', locaux: 8, visites: 4, matchs: 6, loyers: 380000 },
  { mois: 'Jan', locaux: 9, visites: 6, matchs: 7, loyers: 490000 },
  { mois: 'Fév', locaux: 10, visites: 8, matchs: 8, loyers: 560000 },
  { mois: 'Mar', locaux: 12, visites: 11, matchs: 9, loyers: 680000 },
];

const EXPORTS = [
  { label: 'Liste des locaux (Excel)', icon: '📊', desc: 'Tous les locaux avec leurs caractéristiques complètes' },
  { label: 'Rapport de matching (PDF)', icon: '🔗', desc: 'Correspondances locaux-enseignes avec scores détaillés' },
  { label: 'Pipeline visites (Excel)', icon: '📅', desc: 'Historique et planning des visites par broker' },
  { label: 'Inventaire documentaire (PDF)', icon: '📁', desc: 'État des documents par local avec statuts' },
  { label: 'Performance brokers (Excel)', icon: '👥', desc: 'KPIs individuels et comparatifs par période' },
  { label: 'Rapport financier (PDF)', icon: '💰', desc: 'Loyers, pas-de-porte, dépôts de garantie agrégés' },
];

const PERIODS = ['Ce mois', '3 derniers mois', '6 derniers mois', 'Cette année'];

// ─── Excel helper ─────────────────────────────────────────────────────────────
async function downloadExcel(filename: string, sheetName: string, rows: Record<string, any>[]) {
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// ─── PDF helper ───────────────────────────────────────────────────────────────
async function downloadPDF(
  filename: string,
  title: string,
  head: string[],
  body: (string | number)[][]
) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  doc.setFontSize(16);
  doc.setTextColor(30, 58, 95);
  doc.text(title, 14, 16);
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 14, 22);

  autoTable(doc, {
    startY: 28,
    head: [head],
    body,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 14, right: 14 },
  });

  doc.save(filename);
}

export default function ReportingContent() {
  const [period, setPeriod] = useState('6 derniers mois');
  const [exporting, setExporting] = useState<string | null>(null);
  const [stats, setStats] = useState<AppStats | null>(null);

  useEffect(() => {
    fetchStats().then(setStats);
  }, []);

  // ─── Export: Liste des locaux (Excel) ───────────────────────────────────────
  const exportLocauxExcel = async () => {
    const locaux = await locauxService.getAll();
    const rows = locaux.map(l => ({
      'Référence': l.ref,
      'Titre': l.title,
      'Ville': l.city,
      'Quartier': l.quartier,
      'Adresse': l.address,
      'Type': l.type,
      'Surface totale (m²)': l.surfaceTotal,
      'Surface RDC (m²)': l.surfaceRDC,
      'Loyer (MAD)': l.loyer,
      'Façade (m)': l.facade,
      'Hauteur plafond (m)': l.hauteurPlafond,
      'Statut': l.status,
      'Disponibilité': l.disponibilite,
      'Broker': l.broker,
      'Propriétaire': l.proprietaire,
      'Nb visites': l.nbVisites,
      'Score matching': l.bestMatchScore,
      'Meilleur match': l.bestMatchTenant,
      'Complétude (%)': l.completude,
      'Mis à jour': l.updatedAt ? new Date(l.updatedAt).toLocaleDateString('fr-FR') : '',
    }));
    await downloadExcel(`locaux_${new Date().toISOString().slice(0, 10)}.xlsx`, 'Locaux', rows);
  };

  // ─── Export: Rapport de matching (PDF) ──────────────────────────────────────
  const exportMatchingPDF = async () => {
    const locaux = await locauxService.getAll();
    const head = ['Référence', 'Local', 'Ville', 'Surface (m²)', 'Loyer (MAD)', 'Score matching', 'Meilleur match', 'Statut'];
    const body = locaux
      .filter(l => l.bestMatchScore > 0)
      .sort((a, b) => b.bestMatchScore - a.bestMatchScore)
      .map(l => [l.ref, l.title, l.city, l.surfaceTotal, l.loyer, `${l.bestMatchScore}%`, l.bestMatchTenant, l.status]);
    await downloadPDF(
      `matching_${new Date().toISOString().slice(0, 10)}.pdf`,
      'Rapport de Matching — Axxam Retail Listings Listings',
      head,
      body
    );
  };

  // ─── Export: Pipeline visites (Excel) ───────────────────────────────────────
  const exportVisitesExcel = async () => {
    const visites = await visitesService.getAll();
    const rows = visites.map(v => ({
      'Local': v.local,
      'Référence local': v.localRef,
      'Enseigne': v.enseigne,
      'Contact': v.contact,
      'Date': v.date,
      'Heure': v.heure,
      'Broker': v.broker,
      'Statut': v.statut,
      'Priorité': v.priorite,
      'Note': v.note,
    }));
    await downloadExcel(`visites_${new Date().toISOString().slice(0, 10)}.xlsx`, 'Visites', rows);
  };

  // ─── Export: Inventaire documentaire (PDF) ──────────────────────────────────
  const exportDocumentsPDF = async () => {
    const docs = await documentsService.getAll();
    const head = ['Nom', 'Type', 'Local', 'Réf. local', 'Statut', 'Uploadé par', 'Date', 'Taille', 'Version'];
    const body = docs.map(d => [d.nom, d.type, d.local, d.localRef, d.statut, d.uploadedBy, d.date, d.taille, d.version]);
    await downloadPDF(
      `documents_${new Date().toISOString().slice(0, 10)}.pdf`,
      'Inventaire Documentaire — Axxam Retail Listings Listings',
      head,
      body
    );
  };

  // ─── Export: Performance brokers (Excel) ────────────────────────────────────
  const exportBrokersExcel = async () => {
    if (!stats) return;
    const rows = stats.brokerPerf.map(b => ({
      'Broker': b.broker,
      'Locaux gérés': b.locaux,
      'Visites réalisées': b.visites,
      'Deals actifs': b.matchs,
      'Taux de conversion (%)': b.taux,
    }));
    await downloadExcel(`brokers_${new Date().toISOString().slice(0, 10)}.xlsx`, 'Performance Brokers', rows);
  };

  // ─── Export: Rapport financier (PDF) ────────────────────────────────────────
  const exportFinancierPDF = async () => {
    const locaux = await locauxService.getAll();
    const head = ['Référence', 'Local', 'Ville', 'Type', 'Surface (m²)', 'Loyer mensuel (MAD)', 'Loyer annuel (MAD)', 'Statut', 'Disponibilité'];
    const body = locaux.map(l => [
      l.ref,
      l.title,
      l.city,
      l.type,
      l.surfaceTotal,
      l.loyer,
      l.loyer * 12,
      l.status,
      l.disponibilite,
    ]);
    const totalMensuel = locaux.reduce((s, l) => s + l.loyer, 0);
    body.push(['', '', '', '', '', '', '', '', '']);
    body.push(['TOTAL', '', '', '', '', totalMensuel, totalMensuel * 12, '', '']);
    await downloadPDF(
      `financier_${new Date().toISOString().slice(0, 10)}.pdf`,
      'Rapport Financier — Axxam Retail Listings Listings',
      head,
      body
    );
  };

  const handleExport = async (label: string) => {
    setExporting(label);
    try {
      switch (label) {
        case 'Liste des locaux (Excel)':
          await exportLocauxExcel();
          break;
        case 'Rapport de matching (PDF)':
          await exportMatchingPDF();
          break;
        case 'Pipeline visites (Excel)':
          await exportVisitesExcel();
          break;
        case 'Inventaire documentaire (PDF)':
          await exportDocumentsPDF();
          break;
        case 'Performance brokers (Excel)':
          await exportBrokersExcel();
          break;
        case 'Rapport financier (PDF)':
          await exportFinancierPDF();
          break;
        default:
          break;
      }
      toast.success(`Export "${label}" téléchargé avec succès !`);
    } catch (err: any) {
      console.error('Export error:', err);
      toast.error(`Erreur lors de l'export : ${err?.message || 'Erreur inconnue'}`);
    } finally {
      setExporting(null);
    }
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg className="w-8 h-8 animate-spin text-primary-700" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  const highScoreMatchs = Math.round(stats.totalLocaux * stats.tauxMatching / 100);

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">Reporting & Exports</h1>
          <p className="text-sm text-slate-500 mt-0.5">Analyses de performance et exports de données</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-1">
          {PERIODS.map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-xs font-600 rounded-md transition-all ${period === p ? 'bg-primary-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Locaux actifs', value: String(stats.totalLocaux), delta: `${stats.publies + stats.aFortPotentiel} publiés`, color: 'text-primary-700', icon: '🏢' },
          { label: 'Visites réalisées', value: String(stats.visitesRealisees), delta: `${stats.visitesPlanifiees} planifiées`, color: 'text-emerald-600', icon: '📅' },
          { label: 'Taux de matching', value: `${stats.tauxMatching}%`, delta: `${highScoreMatchs}/${stats.totalLocaux} matchs`, color: 'text-amber-600', icon: '🔗' },
          { label: 'Loyer moyen', value: `${stats.avgLoyer} K MAD`, delta: `${stats.totalLocaux} locaux`, color: 'text-violet-600', icon: '💰' },
        ].map(({ label, value, delta, color, icon }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">{icon}</span>
              <p className="text-xs text-slate-400">{label}</p>
            </div>
            <p className={`text-2xl font-700 ${color}`}>{value}</p>
            <p className="text-xs text-slate-400 mt-1">{delta}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-6">
        {/* Monthly trend */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-sm font-700 text-slate-900 mb-4">Évolution mensuelle</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={MONTHLY_DATA}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mois" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="locaux" stroke="#1e3a5f" strokeWidth={2} dot={{ r: 3 }} name="Locaux" />
              <Line type="monotone" dataKey="visites" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Visites" />
              <Line type="monotone" dataKey="matchs" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="Matchs" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Statut distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-sm font-700 text-slate-900 mb-4">Répartition des statuts</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={stats.statutDistribution} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                {stats.statutDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {stats.statutDistribution.map(s => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-slate-600">{s.name}</span>
                </div>
                <span className="font-600 text-slate-800">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* By city */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm mb-6">
        <h3 className="text-sm font-700 text-slate-900 mb-4">Locaux par ville</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={stats.byCity}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="ville" tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
            <Bar dataKey="locaux" fill="#1e3a5f" radius={[4, 4, 0, 0]} name="Locaux" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Broker performance */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm mb-6">
        <h3 className="text-sm font-700 text-slate-900 mb-4">Performance brokers</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="table-th">Broker</th>
                <th className="table-th text-center">Locaux</th>
                <th className="table-th text-center">Visites</th>
                <th className="table-th text-center">Deals actifs</th>
                <th className="table-th text-center">Taux conversion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {stats.brokerPerf.map(b => (
                <tr key={b.broker} className="hover:bg-slate-50/50 transition-colors">
                  <td className="table-td font-600 text-slate-900">{b.broker}</td>
                  <td className="table-td text-center text-sm text-slate-700">{b.locaux}</td>
                  <td className="table-td text-center text-sm text-slate-700">{b.visites}</td>
                  <td className="table-td text-center text-sm text-slate-700">{b.matchs}</td>
                  <td className="table-td text-center">
                    <span className={`text-xs font-700 ${b.taux >= 50 ? 'text-emerald-600' : b.taux >= 25 ? 'text-amber-600' : 'text-slate-400'}`}>
                      {b.taux}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Documents stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Docs total', value: stats.totalDocs, color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' },
          { label: 'Validés', value: stats.docsValides, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
          { label: 'À vérifier', value: stats.docsAVerifier, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
          { label: 'Manquants', value: stats.docsManquants, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
          { label: 'Expirés', value: stats.docsExpires, color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`rounded-xl border p-3 ${bg}`}>
            <p className={`text-xl font-700 ${color}`}>{value}</p>
            <p className={`text-xs font-600 ${color} mt-0.5`}>{label}</p>
          </div>
        ))}
      </div>

      {/* Exports */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h3 className="text-sm font-700 text-slate-900 mb-4">Exports disponibles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {EXPORTS.map(({ label, icon, desc }) => (
            <button
              key={label}
              onClick={() => handleExport(label)}
              disabled={exporting === label}
              className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left group disabled:opacity-60"
            >
              <span className="text-2xl flex-shrink-0">{icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-600 text-slate-900 group-hover:text-primary-700 transition-colors">{label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
              </div>
              {exporting === label ? (
                <svg className="w-4 h-4 animate-spin text-primary-700 ml-auto flex-shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-slate-300 group-hover:text-primary-400 ml-auto flex-shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
